import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useModernTemplateState } from './useModernTemplateState';
import { useModernTemplateCustomizations } from './useModernTemplateCustomizations';
import { useBookingFormState } from './useBookingFormState';
import { useCalendarSlots } from '@/hooks/use-calendar-slots';
import { useCalendarSlotsEnhanced } from '@/hooks/calendar-slots/use-calendar-slots-enhanced';
import { useBookingSubmission } from '@/hooks/use-booking-submission';
import { useHostSettings } from '@/hooks/use-host-settings';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCustomFont, loadCustomFont } from '../utils/templateUtils';
import { useUnifiedLoadingState } from '@/hooks/use-unified-loading-state';
import { useOptimizedDayAvailability } from '@/hooks/calendar-slots/use-optimized-day-availability';
import { DEFAULT_BOOKING_SETTINGS } from '@/lib/constants/booking-defaults';
import { getDateKeyInTimezone } from '@/lib/timezone/date-utils';
import { useBookingContext } from '@/contexts/BookingContext';
import type { ModernTemplateProps } from '../types';

interface ExtendedModernTemplateProps extends ModernTemplateProps {
  useEnhancedSlots?: boolean;
}

export function useModernTemplateHooks({
  meetingLink,
  urlParams = {},
  customizations = {},
  troubleshootMode = false,
  forceMobileLayout = false,
  useEnhancedSlots = false
}: ExtendedModernTemplateProps) {
  // Refs to prevent unnecessary re-renders
  const stablePropsRef = useRef({
    meetingLinkId: meetingLink?.id,
    userId: meetingLink?.user_id,
    useEnhancedSlots
  });

  // Update stable props only when actually changed
  useEffect(() => {
    const newProps = {
      meetingLinkId: meetingLink?.id,
      userId: meetingLink?.user_id,
      useEnhancedSlots
    };
    
    if (JSON.stringify(stablePropsRef.current) !== JSON.stringify(newProps)) {
      stablePropsRef.current = newProps;
    }
  }, [meetingLink?.id, meetingLink?.user_id, useEnhancedSlots]);

  // Basic UI hooks
  const isMobile = useIsMobile();
  const fontClass = useCustomFont(customizations?.customFont);
  
  // Get existing booking context for reschedule mode (optional - only if BookingProvider exists)
  let existingBooking = null;
  let isRescheduleMode = false;
  
  try {
    const bookingContext = useBookingContext();
    existingBooking = bookingContext.existingBooking;
    isRescheduleMode = bookingContext.isRescheduleMode;
  } catch (error) {
    // BookingProvider not found - this is fine for preview/edit pages
    // Use default values: existingBooking = null, isRescheduleMode = false
  }

  // Template state hooks with stable references
  const {
    step,
    selectedDate,
    selectedTime,
    selectedTimezone,
    debugSettings,
    setSelectedTimezone,
    handleDateSelected,
    handleTimeSelected,
    handleBack
  } = useModernTemplateState(meetingLink, urlParams);

  const { themeCustomizations } = useModernTemplateCustomizations(meetingLink, customizations);

  const {
    formData,
    customAnswers,
    guests,
    isSubmitted,
    bookingId,
    setGuests,
    setIsSubmitted,
    setBookingId,
    handleInputChange,
    handleCustomAnswerChange
  } = useBookingFormState(stablePropsRef.current.meetingLinkId);

  const { submitBooking, loading, error } = useBookingSubmission();

  // Host settings with safe defaults - memoized to prevent re-renders
  const { hostSettings, loading: hostSettingsLoading } = useHostSettings(
    stablePropsRef.current.userId,
    {
      startTime: DEFAULT_BOOKING_SETTINGS.START_TIME,
      endTime: DEFAULT_BOOKING_SETTINGS.END_TIME,
      bufferBefore: DEFAULT_BOOKING_SETTINGS.BUFFER_BEFORE,
      bufferAfter: DEFAULT_BOOKING_SETTINGS.BUFFER_AFTER,
      minNoticeTime: DEFAULT_BOOKING_SETTINGS.MIN_NOTICE_TIME,
    },
    selectedDate
  );

  // Stable organizer timezone resolution - heavily memoized
  const organizerTimezone = useMemo(() => {
    if (!meetingLink) return Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const resolvedTimezone = hostSettings?.timezone || 
           hostSettings?.userEnergySettings?.timezone || 
           meetingLink.profiles?.timezone || 
           meetingLink.user_metadata?.timezone || 
           Intl.DateTimeFormat().resolvedOptions().timeZone;

    console.log('[useModernTemplateHooks] Organizer timezone resolution:', {
      meetingLinkId: meetingLink?.id,
      hostSettingsTimezone: hostSettings?.timezone,
      userEnergySettingsTimezone: hostSettings?.userEnergySettings?.timezone,
      profilesTimezone: meetingLink.profiles?.timezone,
      userMetadataTimezone: meetingLink.user_metadata?.timezone,
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      finalTimezone: resolvedTimezone
    });

    return resolvedTimezone;
  }, [
    hostSettings?.timezone ?? '',
    hostSettings?.userEnergySettings?.timezone ?? '',
    meetingLink?.profiles?.timezone ?? '',
    meetingLink?.user_metadata?.timezone ?? ''
  ]);

  // Unified loading state
  const {
    loadingState,
    setInitialLoading,
    setRefreshing,
    setStable,
    shouldShowSkeleton,
    shouldShowRefreshIndicator,
  } = useUnifiedLoadingState();

  // Optimized day availability with timezone parameter
  const { 
    isDayAvailable: optimizedIsDayAvailable, 
    preloadCalendarMonth 
  } = useOptimizedDayAvailability(stablePropsRef.current.userId, organizerTimezone);

  // Generate dates for the next 30 days starting from today - memoized
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, []);

  // Always call both hooks to maintain consistent hook order
  const enhancedSlotsResult = useCalendarSlotsEnhanced({
    hostUserId: stablePropsRef.current.userId,
    energyPreference: meetingLink?.energy_preference || 'focus',
    duration: meetingLink?.duration || 30,
    selectedDates: availableDates,
    timezone: selectedTimezone, // Use selectedTimezone instead of organizerTimezone
    troubleshootMode,
    showAllSlots: meetingLink?.show_all_slots || false,
    excludeEventId: isRescheduleMode ? existingBooking?.calendar_event_id : undefined
  });

  const regularSlotsResult = useCalendarSlots({
    selectedDate: selectedDate || new Date(),
    duration: debugSettings.duration,
    energyPreference: debugSettings.energyPreference,
    hostUserId: stablePropsRef.current.userId || 'debug',
    defaultSettings: debugSettings,
    timezone: selectedTimezone, // Use selectedTimezone instead of organizerTimezone
    includeRawData: troubleshootMode,
    troubleshootMode,
    wakeTime: debugSettings.wakeTime,
    excludeEventId: isRescheduleMode ? existingBooking?.calendar_event_id : undefined
  });

  // Convert enhanced slots format to regular format for compatibility - memoized with timezone-aware date keys
  const availableSlots = useMemo(() => {
    if (useEnhancedSlots && enhancedSlotsResult.availableSlots) {
      // Use timezone-aware date key generation instead of UTC
      const selectedDateKey = selectedDate ? getDateKeyInTimezone(selectedDate, selectedTimezone) : null;
      
      if (troubleshootMode) {
        console.log('Enhanced slots - Selected date:', selectedDate?.toISOString());
        console.log('Enhanced slots - Date key (timezone-aware):', selectedDateKey);
        console.log('Enhanced slots - Selected timezone:', selectedTimezone);
        console.log('Enhanced slots - Available slots keys:', Object.keys(enhancedSlotsResult.availableSlots));
      }
      
      if (selectedDateKey && enhancedSlotsResult.availableSlots[selectedDateKey]) {
        const daySlots = enhancedSlotsResult.availableSlots[selectedDateKey];
        // Combine preferred and available slots into a single array
        return [...daySlots.preferred, ...daySlots.available];
      }
      return [];
    }
    return regularSlotsResult.availableSlots || [];
  }, [useEnhancedSlots, enhancedSlotsResult.availableSlots, regularSlotsResult.availableSlots, selectedDate, selectedTimezone, troubleshootMode]);

  // Enhanced slots data for separate display of preferred vs available - memoized
  const enhancedAvailableSlots = useMemo(() => {
    if (useEnhancedSlots && enhancedSlotsResult.availableSlots) {
      return enhancedSlotsResult.availableSlots;
    }
    return undefined;
  }, [useEnhancedSlots, enhancedSlotsResult.availableSlots]);

  // Loading state coordination effect with stable dependencies
  useEffect(() => {
    const isLoading = useEnhancedSlots ? enhancedSlotsResult.loading : regularSlotsResult.loading;
    if (isLoading && !loadingState.hasLoadedOnce) {
      setInitialLoading();
    } else if (!isLoading) {
      setStable();
    }
  }, [useEnhancedSlots, enhancedSlotsResult.loading, regularSlotsResult.loading, loadingState.hasLoadedOnce, setInitialLoading, setStable]);

  // Enhanced day availability function that uses enhanced slots data - memoized with timezone-aware date keys
  const isDayAvailable = useCallback((date: Date): boolean => {
    if (!stablePropsRef.current.userId || stablePropsRef.current.userId === 'debug') {
      return true;
    }
    
    try {
      if (useEnhancedSlots && enhancedSlotsResult.availableSlots) {
        // Use timezone-aware date key generation instead of UTC
        const dateKey = getDateKeyInTimezone(date, selectedTimezone);
        
        if (troubleshootMode) {
          console.log('isDayAvailable - Date:', date.toISOString());
          console.log('isDayAvailable - Date key (timezone-aware):', dateKey);
          console.log('isDayAvailable - Selected timezone:', selectedTimezone);
        }
        
        const daySlots = enhancedSlotsResult.availableSlots[dateKey];
        if (daySlots) {
          const totalSlots = daySlots.preferred.length + daySlots.available.length;
          
          if (troubleshootMode) {
            console.log('isDayAvailable - Total slots for', dateKey, ':', totalSlots);
            console.log('isDayAvailable - Day config:', {
              preferred: daySlots.preferred.length,
              available: daySlots.available.length,
              dayOfWeek: date.getDay(),
              dateString: date.toDateString()
            });
          }
          
          return totalSlots > 0;
        }
        
        if (troubleshootMode) {
          console.log('isDayAvailable - No slot data found for', dateKey);
        }
        return false;
      }
      
      // Fallback to optimized day availability for regular slots
      return optimizedIsDayAvailable(date) || regularSlotsResult.isDayAvailable?.(date) || false;
    } catch (error) {
      console.error('Error checking day availability:', error);
      return false;
    }
  }, [useEnhancedSlots, enhancedSlotsResult.availableSlots, optimizedIsDayAvailable, regularSlotsResult.isDayAvailable, selectedTimezone, troubleshootMode]);

  // Get the correct refresh function based on which slots we're using - memoized
  const refetchSlots = useCallback(() => {
    if (useEnhancedSlots) {
      return enhancedSlotsResult.refreshSlots
        ? enhancedSlotsResult.refreshSlots()
        : Promise.resolve();
    } else {
      return regularSlotsResult.refetch
        ? regularSlotsResult.refetch()
        : Promise.resolve();
    }
  }, [useEnhancedSlots, enhancedSlotsResult.refreshSlots, regularSlotsResult.refetch]);

  // Load custom font effect with cleanup
  useEffect(() => {
    if (themeCustomizations?.customFont) {
      return loadCustomFont(themeCustomizations.customFont);
    }
  }, [themeCustomizations?.customFont]);

  return {
    // UI State
    isMobile,
    fontClass,
    
    // Template State
    step,
    selectedDate,
    selectedTime,
    selectedTimezone,
    debugSettings,
    setSelectedTimezone,
    handleDateSelected,
    handleTimeSelected,
    handleBack,
    
    // Customizations
    themeCustomizations,
    
    // Form State
    formData,
    customAnswers,
    guests,
    isSubmitted,
    bookingId,
    setGuests,
    setIsSubmitted,
    setBookingId,
    handleInputChange,
    handleCustomAnswerChange,
    
    // Booking
    submitBooking,
    loading,
    error,
    
    // Calendar Data
    availableSlots,
    unavailableSlots: useEnhancedSlots ? enhancedSlotsResult.unavailableSlots || {} : regularSlotsResult.unavailableSlots || {},
    slotsLoading: useEnhancedSlots ? enhancedSlotsResult.loading : regularSlotsResult.loading,
    slotsError: useEnhancedSlots ? enhancedSlotsResult.error : regularSlotsResult.error,
    hasConnection: useEnhancedSlots ? enhancedSlotsResult.hasConnection : regularSlotsResult.hasConnection,
    isDayAvailable,
    refetchSlots,
    
    // Enhanced slots data for separate display
    enhancedAvailableSlots,
    
    // Settings
    hostSettings,
    hostSettingsLoading,
    organizerTimezone,
    
    // Loading States
    loadingState,
    shouldShowSkeleton,
    shouldShowRefreshIndicator,
    
    // Utilities
    preloadCalendarMonth
  };
}
